<script setup>
import { useLicenseStore } from '@/stores/license'

definePage({
  meta: {
    layout: 'default',
    action: 'manage',
    subject: 'License',
  },
})

const licenseStore = useLicenseStore()
const router = useRouter()

const syncing = ref(false)
const resetting = ref(false)
const resetDialog = ref(false)

onMounted(() => {
  licenseStore.fetchStatus()
})

const doSync = async () => {
  syncing.value = true
  try {
    await licenseStore.sync()
  } catch (e) {
    console.error('Sync lisensi gagal:', e)
  } finally {
    syncing.value = false
  }
}

const doReset = async () => {
  resetting.value = true
  try {
    await licenseStore.resetLocal()
    resetDialog.value = false
    router.push({ name: 'license-activate' })
  } catch (e) {
    console.error('Reset lisensi gagal:', e)
  } finally {
    resetting.value = false
  }
}

const formatDate = dateStr => {
  if (!dateStr) return '-'

  return new Date(dateStr).toLocaleString('id-ID', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}
</script>

<template>
  <div>
    <VRow class="mb-4">
      <VCol cols="12">
        <h4 class="text-h4 font-weight-bold">
          Lisensi
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Status aktivasi &amp; feature flag instalasi ini
        </p>
      </VCol>
    </VRow>

    <VRow v-if="!licenseStore.isActivated">
      <VCol cols="12">
        <VAlert
          type="warning"
          variant="tonal"
        >
          Belum ada aktivasi lisensi.
          <RouterLink :to="{ name: 'license-activate' }">
            Aktivasi sekarang
          </RouterLink>
        </VAlert>
      </VCol>
    </VRow>

    <VRow v-else>
      <VCol
        cols="12"
        md="6"
      >
        <VCard>
          <VCardTitle class="d-flex align-center gap-2">
            <VIcon
              :icon="licenseStore.isValid ? 'ri-shield-check-line' : 'ri-shield-cross-line'"
              :color="licenseStore.isValid ? 'success' : 'error'"
            />
            Status Aktivasi
          </VCardTitle>
          <VCardText>
            <VTable density="compact">
              <tbody>
                <tr>
                  <td class="text-medium-emphasis">
                    License Key
                  </td>
                  <td><code>{{ licenseStore.status?.activation?.license_key_masked }}</code></td>
                </tr>
                <tr>
                  <td class="text-medium-emphasis">
                    Edisi
                  </td>
                  <td>
                    <VChip
                      size="small"
                      color="primary"
                      variant="tonal"
                    >
                      {{ licenseStore.status?.activation?.edition }}
                    </VChip>
                  </td>
                </tr>
                <tr>
                  <td class="text-medium-emphasis">
                    Diaktivasi oleh
                  </td>
                  <td>{{ licenseStore.status?.activation?.activated_by?.name ?? '-' }}</td>
                </tr>
                <tr>
                  <td class="text-medium-emphasis">
                    Tanggal Aktivasi
                  </td>
                  <td>{{ formatDate(licenseStore.status?.activation?.activated_at) }}</td>
                </tr>
                <tr>
                  <td class="text-medium-emphasis">
                    Status
                  </td>
                  <td>
                    <VChip
                      size="small"
                      :color="licenseStore.isValid ? 'success' : 'error'"
                    >
                      {{ licenseStore.isValid ? 'Valid' : 'Tidak Valid' }}
                    </VChip>
                  </td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
          <VCardActions>
            <VBtn
              variant="tonal"
              prepend-icon="ri-refresh-line"
              :loading="syncing"
              @click="doSync"
            >
              Sync
            </VBtn>
            <VSpacer />
            <VBtn
              variant="tonal"
              color="error"
              prepend-icon="ri-restart-line"
              @click="resetDialog = true"
            >
              Reset Lokal
            </VBtn>
          </VCardActions>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        md="6"
      >
        <VCard>
          <VCardTitle>Feature Flags</VCardTitle>
          <VCardText>
            <VList density="compact">
              <VListItem
                v-for="flag in licenseStore.featureFlags"
                :key="flag.key"
              >
                <template #prepend>
                  <VIcon
                    :icon="flag.enabled ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'"
                    :color="flag.enabled ? 'success' : 'secondary'"
                  />
                </template>
                <VListItemTitle>{{ flag.key }}</VListItemTitle>
              </VListItem>
              <VListItem v-if="!licenseStore.featureFlags.length">
                <VListItemTitle class="text-medium-emphasis">
                  Belum ada feature flag.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Reset Confirm -->
    <VDialog
      v-model="resetDialog"
      max-width="420"
    >
      <VCard>
        <VCardTitle class="pa-6 pb-2">
          Reset Aktivasi Lokal?
        </VCardTitle>
        <VCardText class="pa-6 pt-0">
          Instalasi ini akan dianggap belum berlisensi sampai diaktivasi ulang. Histori aktivasi sebelumnya tetap tersimpan (tidak dihapus).
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="resetting"
            @click="resetDialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="error"
            :loading="resetting"
            @click="doReset"
          >
            Reset
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
